// src/components/IntelligenceDashboard.tsx
import React, { useState } from 'react';
import { Brain, Zap, TrendingUp, Users, AlertTriangle, Target, DollarSign, Sparkles, Clock, BarChart, Play, ChevronRight } from 'lucide-react';

interface IntelligenceDashboardProps {
  onSelectCapability: (capabilityId: string) => void;
  onDashboardItemClick?: (item: any) => void;
}

const IntelligenceDashboard: React.FC<IntelligenceDashboardProps> = ({ 
  onSelectCapability,
  onDashboardItemClick 
}) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const metrics = [
    { 
      label: 'Active Projects', 
      value: '12', 
      change: '+2', 
      icon: Brain, 
      color: 'text-blue-500', 
      bgColor: 'bg-blue-50',
      type: 'metric',
      action: 'View all projects'
    },
    { 
      label: 'Teams Monitored', 
      value: '8', 
      change: '+1', 
      icon: Users, 
      color: 'text-green-500', 
      bgColor: 'bg-green-50',
      type: 'metric',
      action: 'Analyze team health'
    },
    { 
      label: 'Bottlenecks Found', 
      value: '5', 
      change: '-3', 
      icon: AlertTriangle, 
      color: 'text-orange-500', 
      bgColor: 'bg-orange-50',
      type: 'metric',
      action: 'Diagnose bottlenecks',
      capability: 'automate-root-cause-analysis'
    },
    { 
      label: 'Cost Savings', 
      value: '$2.4M', 
      change: '+18%', 
      icon: DollarSign, 
      color: 'text-purple-500', 
      bgColor: 'bg-purple-50',
      type: 'metric',
      action: 'Run budget simulation',
      capability: 'optimize-it-budget'
    },
    { 
      label: 'AI Coding ROI', 
      value: '240%', 
      change: '+42%', 
      icon: Zap, 
      color: 'text-indigo-500', 
      bgColor: 'bg-indigo-50',
      type: 'metric',
      action: 'Analyze AI tool impact',
      capability: 'ai-coding-impact'
    },
  ];

  const recentAnalyses = [
    { 
      id: 5, 
      name: 'AI Coding Tool ROI Analysis', 
      capability: 'ai-coding-impact',
      status: 'completed',
      time: '3 hours ago',
      insights: 4,
      query: 'What is the ROI of our AI coding assistants?',
      summary: 'Found 240% ROI with 28% productivity improvement'
    },
    { 
      id: 1, 
      name: 'Payments Project Analysis', 
      capability: 'automate-root-cause-analysis',
      status: 'completed',
      time: '2 hours ago',
      insights: 3,
      query: 'Why is the Payments project delayed?',
      summary: 'Identified 3 critical dependencies causing delays'
    },
    { 
      id: 2, 
      name: 'Generate Capacity Plan Simulation', 
      capability: 'generate-capacity-plan',
      status: 'completed',
      time: '5 hours ago',
      insights: 5,
      query: 'What happens if we move Sarah to AI Fraud Detection?',
      summary: 'Simulated move impact: 78% success probability'
    },
    { 
      id: 3, 
      name: 'Q4 Budget Simulation', 
      capability: 'optimize-it-budget',
      status: 'in-progress',
      time: '1 day ago',
      insights: 2,
      query: 'Simulate 10% budget cut on Engineering',
      summary: 'Potential savings: $450K with minimal impact'
    },
    { 
      id: 4, 
      name: 'AI Initiative Planning', 
      capability: 'match-internal-talent',
      status: 'pending',
      time: '2 days ago',
      insights: 4,
      query: 'Plan a new AI integration project',
      summary: 'Recommended team: 7 people, $500K budget'
    },
  ];

  const quickActions = [
    { 
      label: 'Analyze AI Coding Impact', 
      capability: 'ai-coding-impact', 
      icon: Zap,
      description: 'Measure productivity and ROI from AI coding tools',
      actionText: 'Analyze Now'
    },
    { 
      label: 'Run Team Analysis', 
      capability: 'generate-capacity-plan', 
      icon: Users,
      description: 'Analyze team composition and performance',
      actionText: 'Start Analysis'
    },
    { 
      label: 'Check Project Health', 
      capability: 'automate-root-cause-analysis', 
      icon: AlertTriangle,
      description: 'Identify blockers and delays',
      actionText: 'Diagnose Now'
    },
    { 
      label: 'Plan New Initiative', 
      capability: 'match-internal-talent', 
      icon: Target,
      description: 'Plan and staff new projects',
      actionText: 'Start Planning'
    },
    { 
      label: 'Simulate Budget', 
      capability: 'optimize-it-budget', 
      icon: DollarSign,
      description: 'Test financial scenarios',
      actionText: 'Run Simulation'
    },
  ];

  const getCapabilityColor = (capability: string) => {
    switch (capability) {
      case 'generate-capacity-plan': return 'bg-blue-100 text-blue-700';
      case 'automate-root-cause-analysis': return 'bg-orange-100 text-orange-700';
      case 'match-internal-talent': return 'bg-green-100 text-green-700';
      case 'optimize-it-budget': return 'bg-purple-100 text-purple-700';
      case 'ai-coding-impact': return 'bg-indigo-100 text-indigo-700'; 
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleMetricClick = (metric: any) => {
    if (metric.capability) {
      onSelectCapability(metric.capability);
      if (onDashboardItemClick) {
        onDashboardItemClick({
          type: 'metric',
          data: metric,
          message: metric.action
        });
      }
    }
  };

  const handleAnalysisClick = (analysis: any) => {
    onSelectCapability(analysis.capability);
    if (onDashboardItemClick) {
      onDashboardItemClick({
        type: 'analysis',
        data: analysis,
        message: analysis.query
      });
    }
  };

  const handleQuickActionClick = (action: any) => {
    onSelectCapability(action.capability);
    if (onDashboardItemClick) {
      onDashboardItemClick({
        type: 'quick-action',
        data: action,
        message: `${action.label}: ${action.description}`
      });
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Intelligence Dashboard</h1>
              <p className="text-gray-600 mt-2">Real-time insights from your organizational intelligence platform</p>
            </div>
            <button 
              onClick={() => onSelectCapability('general')}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
            >
              <Sparkles size={18} />
              <span>New Analysis</span>
            </button>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div 
                key={index} 
                onClick={() => handleMetricClick(metric)}
                className={`bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md hover:border-blue-300 cursor-pointer transition-all ${
                  metric.capability ? 'hover:scale-[1.02]' : ''
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <Icon className={metric.color} size={20} />
                  </div>
                  <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                    metric.change.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {metric.change}
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                <p className="text-sm text-gray-600 mt-1">{metric.label}</p>
                {metric.capability && (
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <p className="text-xs text-blue-600 flex items-center">
                      <ChevronRight size={12} className="mr-1" />
                      {metric.action}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
                {/* AI Critical Insights */}
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100 p-6 mt-6">
                  <h3 className="font-bold text-gray-900 mb-3">AI Critical Insights</h3>
                  <div className="space-y-4">
                    {/* AI Coding Productivity Insight */}
                    <div 
                      onClick={() => {
                        onSelectCapability('ai-coding-impact');
                        if (onDashboardItemClick) {
                          onDashboardItemClick({
                            type: 'insight',
                            data: { insight: 'AI coding tool underutilization' },
                            message: 'Analyze AI coding assistant usage: only 40% of developers use it daily'
                          });
                        }
                      }}
                      className="p-3 bg-white border-l-4 border-indigo-500 rounded-r-lg hover:bg-indigo-50 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">🤖 AI coding tools underutilized by developers</p>
                        <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">
                          PRODUCTIVITY
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Only 40% use daily • Potential gain: 28% productivity • Needs training</p>
                    </div> 
                    
                    {/* Support Operations Insight */}
                    <div 
                      onClick={() => {
                        onSelectCapability('automate-root-cause-analysis');
                        if (onDashboardItemClick) {
                          onDashboardItemClick({
                            type: 'insight',
                            data: { insight: 'Support ticket backlog' },
                            message: 'Analyze 65% increase in customer support ticket backlog'
                          });
                        }
                      }}
                      className="p-3 bg-white border-l-4 border-purple-500 rounded-r-lg hover:bg-purple-50 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900">📊 Support ticket backlog increased 65%</p>
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                          OPERATIONS
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Avg resolution: 48h • SLA breach risk: High • Needs resource reallocation</p>
                    </div>
                {/* Customer Issue - Critical */}
                <div 
                  onClick={() => {
                    onSelectCapability('automate-root-cause-analysis');
                    if (onDashboardItemClick) {
                      onDashboardItemClick({
                        type: 'insight',
                        data: { insight: 'Payment failures spike', priority: 'P0' },
                        message: 'Analyze payment failure spike: 320% increase in 24 hours affecting 2,143 users'
                      });
                    }
                  }}
                  className="p-3 bg-white border-l-4 border-red-500 rounded-r-lg hover:bg-red-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">🚨 Payment failures spiked 320% in 24h</p>
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                      P0 CRITICAL
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Affected users: 2,143 • Action: Immediate escalation required</p>
                </div>                                
                
                {/* Team Capacity Insight */}
                <div 
                  onClick={() => {
                    onSelectCapability('generate-capacity-plan');
                    if (onDashboardItemClick) {
                      onDashboardItemClick({
                        type: 'insight',
                        data: { insight: 'Lead engineer underutilization' },
                        message: 'Move John Smith from Payments to AI Fraud Detection: current utilization 45%'
                      });
                    }
                  }}
                  className="p-3 bg-white border-l-4 border-blue-500 rounded-r-lg hover:bg-blue-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">👥 Lead Engineer underutilized in Payments</p>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                      OPTIMIZATION
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">John Smith utilization: 45% • Recommend: Move to AI Fraud Detection</p>
                </div>                                
                
                {/* Budget Optimization Insight */}
                <div 
                  onClick={() => {
                    onSelectCapability('optimize-it-budget');
                    if (onDashboardItemClick) {
                      onDashboardItemClick({
                        type: 'insight',
                        data: { insight: 'Cloud storage cost spike' },
                        message: 'Simulate cloud storage cost optimization: 45% month-over-month increase'
                      });
                    }
                  }}
                  className="p-3 bg-white border-l-4 border-green-500 rounded-r-lg hover:bg-green-50 cursor-pointer transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">💰 Cloud storage costs up 45% MoM</p>
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                      SAVINGS
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Driver: Log retention • Optimization potential: $65K/month</p>
                </div>
                                              
              </div>
              
              {/* View More Link */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <button className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center group">
                  <span>View all 23 critical insights</span>
                  <ChevronRight size={14} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Recent Analyses */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-gray-900">Recent Analyses</h3>
                <button className="text-sm text-blue-600 hover:text-blue-800">View All →</button>
              </div>
              
              <div className="space-y-4">
                {recentAnalyses.map((analysis) => (
                  <div 
                    key={analysis.id} 
                    onClick={() => handleAnalysisClick(analysis)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-200 hover:bg-blue-50 cursor-pointer transition-all"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getCapabilityColor(analysis.capability)}`}>
                          {analysis.capability.replace('-', ' ')}
                        </div>
                        <div className={`px-2 py-1 rounded text-xs ${getStatusColor(analysis.status)}`}>
                          {analysis.status}
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock size={14} className="mr-1" />
                        {analysis.time}
                      </div>
                    </div>
                    
                    <h4 className="font-medium text-gray-900 mb-2">{analysis.name}</h4>
                    <p className="text-sm text-gray-600 mb-3">{analysis.summary}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                          <Brain size={14} className="text-blue-500" />
                          <span className="text-sm text-gray-600">{analysis.insights} insights</span>
                        </div>
                        <div className="text-sm text-gray-500 italic">
                          "{analysis.query}"
                        </div>
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1">
                        <span>Open in Chat</span>
                        <Sparkles size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Intelligence Chart Placeholder */}
              <div className="mt-8 pt-6 border-t">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <BarChart size={18} className="mr-2 text-gray-500" />
                  Intelligence Usage Trend
                </h4>
                <div className="h-48 bg-gradient-to-b from-gray-50 to-white rounded-lg border border-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="inline-block p-4 bg-blue-50 rounded-full mb-3">
                      <TrendingUp size={24} className="text-blue-500" />
                    </div>
                    <p className="text-gray-600">Weekly analysis volume increasing</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">+42% this month</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntelligenceDashboard;